import { useTranslations } from '@lang';
import { Guest } from 'shared-types/Basket';
import { AddExistingGuests } from '../../components/AddExistingGuests';
import { SectionWrapper } from '@common/components/BookingForms/Section';
import { NotesWrapper } from './MultiRoomGuestDetailsStep.styles';
import { RoomPartial } from './MultiRoomGuestDetailsStep.types';
import { useFormContext } from 'react-hook-form';
import { GuestDetails } from '../../components/GuestForm/GuestDetails';
import { NotesField } from '../../components/NotesField';
import { Title } from '@common/components/BookingForms';
import { some } from '@utils/some';
import { ExtrasSection } from '../ExtrasStep';
import { TitleWrapper } from '../GuestDetailsStep/GuestDetailsStep.styles';
import { useAddExistingGuest } from '../hooks/useAddExistingGuest';
import { useStepFooter } from '../hooks/useStepFooter';
import { useGuestDetailsUpdate } from '../hooks/useGuestDetailsUpdate';
import { MappedGuest } from '../../types';
import { mapLoggedInUserToGuest } from '../../helpers/mapLoggedInUserToGuest';
import { validateGuestDetails } from '../../helpers/validateGuestDetails';
import { useState } from 'react';
import { usePackageConfiguration } from '../../contexts';

export const MultiRoomGuestDetailsStep = () => {
  const { t } = useTranslations();

  const {
    journeyParams,
    room,
    availableExtras = [],
    basketGuests,
    loggedInGuest,
  } = usePackageConfiguration();

  const { watch } = useFormContext();
  const guests = watch('guests') || [];
  const isValid = validateGuestDetails(guests);
  const mappedloggedInGuestData = mapLoggedInUserToGuest(loggedInGuest);
  const [availableGuests, setAvailableGuests] = useState<MappedGuest[]>(
    mappedloggedInGuestData ? [mappedloggedInGuestData] : []
  );
  const { handleGuestPillClick, selectedGuests, existingGuests } = useAddExistingGuest({
    availableGuests,
    basketGuests,
  });

  const updateAvailableGuestsOnSubmit = useGuestDetailsUpdate(availableGuests, setAvailableGuests);

  const footer = useStepFooter({ isValid, onButtonClick: updateAvailableGuestsOnSubmit });

  const selectedEntity = room;

  return selectedEntity ? (
    <form noValidate>
      <SectionWrapper>
        <AddExistingGuests
          selectedGuests={selectedGuests}
          existingGuests={existingGuests as Guest[]}
          isGenderSpecific={false}
          guest={loggedInGuest as MappedGuest}
          handleGuestPillClick={handleGuestPillClick}
        />
        {(journeyParams?.rooms || []).flatMap((r: RoomPartial, roomIndex: number) => {
          const startingIndex = (journeyParams?.rooms || [])
            .slice(0, roomIndex)
            .reduce((sum, room) => sum + room.adults, 0);

          return [
            <div key={`room-header-${roomIndex}`}>
              {`${t('common.room')} ${r.roomNumber}`}
              {r.children ? <> +{t('basket.children', { children: r.children })}</> : null}
            </div>,
            ...Array.from({ length: r.adults }, (_, localIndex) => {
              const guestIndex = startingIndex + localIndex;
              return (
                <GuestDetails
                  key={`guest-${guestIndex}`}
                  isGenderSpecific={false}
                  guestIndex={guestIndex}
                  isDisabled={false}
                  withGenderSelect={false}
                  roomNumber={r.roomNumber - 1}
                />
              );
            }),
          ];
        })}
        <NotesWrapper>
          <NotesField name="roomNotes" label={t('rooms.notes')} />
        </NotesWrapper>
      </SectionWrapper>
      {some(availableExtras) && (
        <>
          <TitleWrapper>
            <Title titleId={'room.extrasStepTitle'} />
          </TitleWrapper>
          <ExtrasSection />
        </>
      )}
      {footer}
    </form>
  ) : null;
};
